import React, { useMemo, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  EditOutlined,
  ReloadOutlined,
  RollbackOutlined,
  SendOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkflowStrip, WorkspaceLayout } from '@/components/enterprise';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import EntityAttachmentsCard from '@/components/documents/EntityAttachmentsCard';
import { ENTITY_TYPE_HR_TIMESHEET_MONTH } from '@/constants/approval-entity-types';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { projectService } from '@/services/project.service';
import { useAuthStore } from '@/store/authStore';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { buildLinearWorkflow, hrmsStatusColor, showApprovalPanel } from './shared/hrmsStatus';
import { getOrFindFromList } from './shared/hrmsFetch';

const { Text } = Typography;

const DAY_STATUS_COLOR: Record<string, string> = {
  Present: 'green',
  HalfDay: 'gold',
  Leave: 'blue',
  Holiday: 'purple',
  WeeklyOff: 'default',
  Absent: 'red',
};

const EDITABLE = new Set(['Draft', 'Rejected']);

const DAY_STATUS_OPTIONS = [
  { value: 'Present', label: 'Present' },
  { value: 'HalfDay', label: 'Half day' },
  { value: 'Absent', label: 'Absent (unpaid)' },
  { value: 'Leave', label: 'On approved leave' },
  { value: 'Holiday', label: 'Public / company holiday' },
  { value: 'WeeklyOff', label: 'Weekly off' },
];

const TIME_RULE = { pattern: /^([01]\d|2[0-3]):[0-5]\d$/, message: 'HH:mm' };

const AttendanceMonthDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const { empLabel } = useEmployeeOptions({ employment_status: null });
  const { user } = useAuthStore();
  const canRun = Boolean(user?.is_super_admin) || Boolean(user?.permissions?.includes('hr.attendance.manage'));
  const [loading, setLoading] = useState<string | null>(null);
  const [editingDay, setEditingDay] = useState<any | null>(null);
  const [dayForm] = Form.useForm();
  const [reopenForm] = Form.useForm();

  const { data: projects = [] } = useQuery({
    queryKey: ['projects', 'selection', 'attendance'],
    queryFn: () => projectService.getProjectsForSelection(),
    staleTime: 5 * 60 * 1000,
    enabled: Boolean(editingDay),
  });
  const projectOptions = useMemo(
    () =>
      (projects || []).map((p: any) => ({
        value: p.id,
        label: `${p.project_number || p.project_name || 'Project'} — ${p.project_name || ''}`.trim(),
        code: p.project_number || '',
      })),
    [projects]
  );

  const { data: row, isLoading } = useQuery({
    queryKey: hrmsKeys.attendanceMonth(id || ''),
    queryFn: () =>
      getOrFindFromList(
        () => hrmsService.getAttendanceMonth(id!),
        () => hrmsService.listAttendanceMonths(),
        id!
      ),
    enabled: Boolean(id),
  });

  const period = row ? `${row.period_year}-${String(row.period_month).padStart(2, '0')}` : '';
  const dayParams = row
    ? {
        employee_id: row.employee_id,
        from: `${period}-01`,
        to: dayjs(`${period}-01`).endOf('month').format('YYYY-MM-DD'),
      }
    : undefined;
  const { data: days = [], isLoading: daysLoading } = useQuery({
    queryKey: hrmsKeys.attendanceDays(dayParams),
    queryFn: () => hrmsService.listAttendanceDays(dayParams),
    enabled: Boolean(dayParams),
  });

  const workflow = useMemo(
    () => buildLinearWorkflow(['Draft', 'Submitted', 'Approved'], row?.status || 'Draft'),
    [row?.status]
  );

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.attendanceMonth(id!) });
    await queryClient.invalidateQueries({ queryKey: hrmsKeys.attendanceMonths() });
    await queryClient.invalidateQueries({ queryKey: ['hrms', 'attendance-days'] });
  };

  const run = async (key: string, fn: () => Promise<unknown>, success: string) => {
    setLoading(key);
    try {
      await fn();
      message.success(success);
      await refresh();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Action failed');
    } finally {
      setLoading(null);
    }
  };

  if (isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Monthly timesheet" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }
  if (!row) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Monthly timesheet" />}>
        <Card>Not found</Card>
      </WorkspaceLayout>
    );
  }

  const periodLabel = dayjs(`${period}-01`).format('MMM YYYY');
  const employeeLabel = row.employee_name ? `${row.employee_code} — ${row.employee_name}` : empLabel(row.employee_id);

  const regenerate = () =>
    modal.confirm({
      title: `Regenerate ${periodLabel} for ${employeeLabel}?`,
      content:
        'Previously auto-generated days are rebuilt from the current shifts, holidays, approved leave, approved OT and project allocations. Days entered manually or imported are kept.',
      okText: 'Regenerate',
      onOk: () =>
        run(
          'regen',
          () =>
            hrmsService.generateTimesheets({
              year: row.period_year,
              month: row.period_month,
              employee_ids: [row.employee_id],
              dry_run: false,
            }),
          'Timesheet regenerated'
        ),
    });

  const canEdit = canRun && EDITABLE.has(row.status);

  const openEditDay = (d: any) => {
    setEditingDay(d);
    dayForm.setFieldsValue({
      status: d.status || 'Present',
      check_in: d.check_in ? String(d.check_in).slice(0, 5) : undefined,
      check_out: d.check_out ? String(d.check_out).slice(0, 5) : undefined,
      worked_hours: Number(d.worked_hours || 0),
      ot_hours: Number(d.ot_hours || 0),
      notes: d.notes || undefined,
      project_hours: (d.project_hours || []).map((p: any) => ({
        project_id: p.project_id || undefined,
        project_code: p.project_code || undefined,
        hours: Number(p.hours || 0),
      })),
    });
  };

  const saveDay = async () => {
    const v = await dayForm.validateFields();
    const workless = ['Absent', 'Leave', 'Holiday', 'WeeklyOff'].includes(v.status);
    setLoading('day');
    try {
      await hrmsService.upsertAttendanceDay({
        employee_id: row.employee_id,
        attendance_date: dayjs(editingDay.attendance_date).format('YYYY-MM-DD'),
        status: v.status,
        check_in: workless ? null : v.check_in || null,
        check_out: workless ? null : v.check_out || null,
        worked_hours: workless ? 0 : Number(v.worked_hours || 0),
        ot_hours: Number(v.ot_hours || 0),
        notes: v.notes || null,
        source: 'Manual',
        project_hours: (v.project_hours || [])
          .filter((p: any) => Number(p.hours) > 0 && (p.project_id || p.project_code))
          .map((p: any) => ({
            project_id: p.project_id || undefined,
            project_code: projectOptions.find((o) => o.value === p.project_id)?.code || p.project_code || undefined,
            hours: Number(p.hours),
          })),
      });
      await hrmsService.recalculateAttendanceMonth(row.id);
      message.success(`${dayjs(editingDay.attendance_date).format('DD MMM')} updated — totals recalculated`);
      setEditingDay(null);
      await refresh();
    } catch (e: any) {
      message.error(e?.response?.data?.message || e?.message || 'Save failed');
    } finally {
      setLoading(null);
    }
  };

  const reopen = () =>
    modal.confirm({
      title: `Reopen the approved ${periodLabel} timesheet?`,
      content: (
        <Form form={reopenForm} layout="vertical" style={{ marginTop: 12 }}>
          <Text type="secondary">
            It returns to Draft so HR can correct days, then it must be submitted and approved again.
          </Text>
          <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Reason is required' }]}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      ),
      okText: 'Reopen',
      onOk: async () => {
        const { reason } = await reopenForm.validateFields();
        await run('reopen', () => hrmsService.reopenAttendanceMonth(row.id, reason), 'Timesheet reopened for correction');
        reopenForm.resetFields();
      },
    });

  const dayColumns: ColumnsType<any> = [
    {
      title: 'Date',
      dataIndex: 'attendance_date',
      width: 120,
      render: (d: string) => dayjs(d).format('ddd DD MMM'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s: string) => <Tag color={DAY_STATUS_COLOR[s] || 'default'}>{s}</Tag>,
    },
    {
      title: 'In – Out',
      key: 'inout',
      width: 120,
      render: (_, r) => (r.check_in ? `${String(r.check_in).slice(0, 5)} – ${String(r.check_out || '').slice(0, 5)}` : '—'),
    },
    { title: 'Hours', dataIndex: 'worked_hours', width: 70, align: 'right', render: (v) => Number(v || 0) },
    { title: 'OT', dataIndex: 'ot_hours', width: 60, align: 'right', render: (v) => Number(v || 0) || '' },
    {
      title: 'Projects',
      key: 'projects',
      width: 200,
      render: (_, r) =>
        (r.project_hours || []).length ? (
          <Space direction="vertical" size={0}>
            {r.project_hours.map((p: any) => (
              <Text key={p.id} style={{ fontSize: 12 }}>
                {p.project_code || 'Project'} · {Number(p.hours)}h
              </Text>
            ))}
          </Space>
        ) : (
          ''
        ),
    },
    { title: 'Notes', dataIndex: 'notes', ellipsis: true },
    {
      title: 'Source',
      dataIndex: 'source',
      width: 100,
      render: (s: string) => (s === 'AutoGenerated' ? <Tag color="purple">Auto</Tag> : <Tag>{s || 'Manual'}</Tag>),
    },
    ...(canEdit
      ? [
          {
            title: '',
            key: 'edit',
            width: 70,
            fixed: 'right' as const,
            render: (_: unknown, r: any) => (
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEditDay(r)}>
                Edit
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Monthly timesheet"
          documentNumber={periodLabel}
          statusTag={<Tag color={hrmsStatusColor(row.status)}>{row.status}</Tag>}
          subtitle={<Text type="secondary">{employeeLabel}</Text>}
          primaryActions={
            <Space wrap>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(`/hr/attendance?tab=months&month=${period}`)}>
                Back
              </Button>
              {canRun && EDITABLE.has(row.status) && (
                <>
                  <Button icon={<ThunderboltOutlined />} loading={loading === 'regen'} onClick={regenerate}>
                    Regenerate
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    loading={loading === 'recalc'}
                    onClick={() =>
                      run(
                        'recalc',
                        () => hrmsService.recalculateAttendanceMonth(row.id),
                        row.status === 'Rejected' ? 'Recalculated and returned to Draft' : 'Totals recalculated'
                      )
                    }
                  >
                    {row.status === 'Rejected' ? 'Recalculate & reopen' : 'Recalculate totals'}
                  </Button>
                </>
              )}
              {canRun && row.status === 'Approved' && (
                <Button icon={<RollbackOutlined />} loading={loading === 'reopen'} onClick={reopen}>
                  Reopen for correction
                </Button>
              )}
              {row.status === 'Draft' && (
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  loading={loading === 'submit'}
                  onClick={() => run('submit', () => hrmsService.submitAttendanceMonth(row.id), 'Submitted')}
                >
                  Submit
                </Button>
              )}
            </Space>
          }
        />
      }
      workflow={<WorkflowStrip stages={workflow.stages} current={workflow.current} compact />}
    >
      <Row gutter={16}>
        <Col xs={24} lg={16}>
          <Card title="Timesheet summary" style={{ marginBottom: 16 }}>
            <Descriptions column={{ xs: 1, sm: 2, md: 3 }} size="small">
              <Descriptions.Item label="Employee">{employeeLabel}</Descriptions.Item>
              <Descriptions.Item label="Period">{periodLabel}</Descriptions.Item>
              <Descriptions.Item label="Days recorded">{row.days_recorded ?? days.length}</Descriptions.Item>
              <Descriptions.Item label="Present">{Number(row.days_present || 0)}</Descriptions.Item>
              <Descriptions.Item label="Leave">{Number(row.days_leave || 0)}</Descriptions.Item>
              <Descriptions.Item label="Absent">{Number(row.days_absent || 0)}</Descriptions.Item>
              <Descriptions.Item label="Holidays">{row.days_holiday ?? 0}</Descriptions.Item>
              <Descriptions.Item label="Weekly off">{row.days_weekly_off ?? 0}</Descriptions.Item>
              <Descriptions.Item label="Worked hours">{Number(row.total_worked_hours || 0)}</Descriptions.Item>
              <Descriptions.Item label="OT hours">{Number(row.total_ot_hours || 0)}</Descriptions.Item>
              <Descriptions.Item label="Late minutes">{row.total_late_minutes ?? 0}</Descriptions.Item>
              <Descriptions.Item label="Generated">
                {row.generated_at ? dayjs(row.generated_at).format('DD MMM YYYY HH:mm') : 'From daily register'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
          <Card
            title="Daily attendance"
            style={{ marginBottom: 16 }}
            extra={
              EDITABLE.has(row.status) ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Edited days are kept when the month is regenerated.
                </Text>
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Locked while {row.status}
                  {row.status === 'Approved' ? ' — use Reopen for correction' : ' — reject it to edit'}
                </Text>
              )
            }
          >
            <Table
              rowKey="id"
              size="small"
              loading={daysLoading}
              columns={dayColumns}
              dataSource={days}
              pagination={false}
              scroll={{ x: 900 }}
            />
          </Card>
          <EntityAttachmentsCard entityType="HR_TIMESHEET_MONTH" entityId={row.id} />
          <Modal
            open={Boolean(editingDay)}
            title={editingDay ? `Edit ${dayjs(editingDay.attendance_date).format('ddd DD MMM YYYY')}` : ''}
            okText="Save day"
            confirmLoading={loading === 'day'}
            onOk={saveDay}
            onCancel={() => setEditingDay(null)}
            width={640}
            destroyOnClose
          >
            <Form form={dayForm} layout="vertical">
              <Space wrap size="middle">
                <Form.Item name="status" label="Status" rules={[{ required: true }]}>
                  <Select style={{ width: 200 }} options={DAY_STATUS_OPTIONS} />
                </Form.Item>
                <Form.Item name="check_in" label="In" rules={[TIME_RULE]}>
                  <Input placeholder="HH:mm" style={{ width: 90 }} />
                </Form.Item>
                <Form.Item name="check_out" label="Out" rules={[TIME_RULE]}>
                  <Input placeholder="HH:mm" style={{ width: 90 }} />
                </Form.Item>
                <Form.Item name="worked_hours" label="Worked hrs">
                  <InputNumber min={0} max={24} step={0.25} style={{ width: 90 }} />
                </Form.Item>
                <Form.Item name="ot_hours" label="OT hrs">
                  <InputNumber min={0} max={24} step={0.25} style={{ width: 90 }} />
                </Form.Item>
              </Space>
              <Form.List name="project_hours">
                {(fields, { add, remove }) => (
                  <>
                    <Text strong style={{ display: 'block', marginBottom: 8 }}>
                      Project hours
                    </Text>
                    {fields.map((field) => (
                      <Space key={field.key} align="start" style={{ display: 'flex', marginBottom: 4 }}>
                        <Form.Item
                          name={[field.name, 'project_id']}
                          rules={[{ required: true, message: 'Project' }]}
                          style={{ marginBottom: 8 }}
                        >
                          <Select
                            placeholder="Project"
                            style={{ width: 360 }}
                            options={projectOptions}
                            showSearch
                            optionFilterProp="label"
                          />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'hours']}
                          rules={[{ required: true, message: 'Hours' }]}
                          style={{ marginBottom: 8 }}
                        >
                          <InputNumber min={0.25} max={24} step={0.25} placeholder="Hours" style={{ width: 90 }} />
                        </Form.Item>
                        <Button type="link" danger onClick={() => remove(field.name)}>
                          Remove
                        </Button>
                      </Space>
                    ))}
                    <Button size="small" onClick={() => add({ hours: 8 })} style={{ marginBottom: 12 }}>
                      Add project
                    </Button>
                  </>
                )}
              </Form.List>
              <Form.Item name="notes" label="Notes">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Form>
          </Modal>
        </Col>
        <Col xs={24} lg={8}>
          {showApprovalPanel(row) && (
            <ApprovalPanel
              entityType={ENTITY_TYPE_HR_TIMESHEET_MONTH}
              entityId={row.id}
              extraInvalidateQueryKeys={[hrmsKeys.attendanceMonth(id!), hrmsKeys.attendanceMonths()]}
            />
          )}
        </Col>
      </Row>
    </WorkspaceLayout>
  );
};

export default AttendanceMonthDetailPage;
